import { DatePicker } from 'components/FormUtils/DatePicker'
import ReactSelectWrapper from 'components/FormUtils/ReactSelectWrapper'
import { BaseTextInput } from 'components/FormUtils/TextInput'
import { ColorPicker, type ColorpickerColor } from 'components/ColorPicker'
import type { User as UserType } from 'services/users'
import { isLimitedUser } from 'models/user'
import { MULTI_VALUE_SELECT_COLORS } from 'utils/constants'

export interface CampaignOwnerOption {
  value: number
  label: string
  email: string
  initials: string
}

export interface CampaignSelectOption<TValue extends string | number> {
  value: TValue
  label: string
}

const fieldLabelClassName =
  'mb-1 block select-none text-base font-medium text-fg'

/** Shared option renderer for user selects: avatar + name + email in the
 * menu, plain label as the selected value. */
function formatUserOptionLabel(
  option: unknown,
  { context }: { context: 'menu' | 'value' }
) {
  const userOption = option as CampaignOwnerOption

  if (context === 'value') {
    return userOption.label
  }

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2.5">
        <div className="flex size-7 items-center justify-center rounded-full bg-surface-active text-xxs font-medium text-brand">
          {userOption.initials}
        </div>
        <div className="min-w-0">
          <div className="truncate text-sm text-fg">{userOption.label}</div>
          <div className="truncate text-xs text-fg-muted">
            {userOption.email}
          </div>
        </div>
      </div>
    </div>
  )
}

interface CampaignFormFieldsV2Props<
  TCampaignType extends string,
  TProduct extends number,
> {
  name: string
  onNameChange: (value: string) => void
  campaignTypeOptions: CampaignSelectOption<TCampaignType>[]
  campaignTypeValue: TCampaignType | null
  onCampaignTypeChange: (value: TCampaignType | null) => void
  campaignTypeLoading?: boolean
  campaignTypeDisabled?: boolean
  hideCampaignType?: boolean
  launchDate: string
  onLaunchDateChange: (value: string) => void
  launchDateType: 'set' | 'skip'
  onLaunchDateTypeChange: (value: 'set' | 'skip') => void
  launchDateDisabled?: boolean
  ownerOptions: CampaignOwnerOption[]
  ownerValue: number | null
  onOwnerChange: (value: number | null) => void
  ownerLoading?: boolean
  ownerDisabled?: boolean
  /**
   * Options for the notification-recipients select. Unlike `ownerOptions`,
   * this list is NOT filtered to owner-eligible users — a limited user can still be
   * a notification recipient (FIR-8017 restricts ownership, not notifications).
   * Defaults to `ownerOptions` when omitted.
   */
  recipientOptions?: CampaignOwnerOption[]
  productOptions: CampaignSelectOption<TProduct>[]
  productValue: TProduct | null
  onProductChange: (value: TProduct | null) => void
  productLoading?: boolean
  productDisabled?: boolean
  description: string
  onDescriptionChange: (value: string) => void
  descriptionDisabled?: boolean
  color: ColorpickerColor
  onColorChange: (value: ColorpickerColor) => void
  colorDisabled?: boolean
  /**
   * Optional notification recipients multi-select, rendered only when
   * onNotificationRecipientsChange is provided. Uses recipientOptions for the
   * selectable users. Empty selection means notifications go to the campaign
   * owner (the default behavior).
   */
  notificationRecipientIds?: number[]
  onNotificationRecipientsChange?: (value: number[]) => void
}

export function CampaignFormFieldsV2<
  TCampaignType extends string,
  TProduct extends number,
>({
  name,
  onNameChange,
  campaignTypeOptions,
  campaignTypeValue,
  onCampaignTypeChange,
  campaignTypeLoading = false,
  campaignTypeDisabled = false,
  hideCampaignType = false,
  launchDate,
  onLaunchDateChange,
  launchDateType,
  onLaunchDateTypeChange,
  launchDateDisabled = false,
  ownerOptions,
  ownerValue,
  onOwnerChange,
  ownerLoading = false,
  ownerDisabled = false,
  recipientOptions = ownerOptions,
  productOptions,
  productValue,
  onProductChange,
  productLoading = false,
  productDisabled = false,
  description,
  onDescriptionChange,
  descriptionDisabled = false,
  color,
  onColorChange,
  colorDisabled = false,
  notificationRecipientIds = [],
  onNotificationRecipientsChange,
}: CampaignFormFieldsV2Props<TCampaignType, TProduct>) {
  const selectedCampaignTypeOption =
    campaignTypeOptions.find((option) => option.value === campaignTypeValue) ??
    null
  const selectedOwnerOption =
    ownerOptions.find((option) => option.value === ownerValue) ?? null
  const selectedProductOption =
    productOptions.find((option) => option.value === productValue) ?? null
  const selectedNotificationRecipientOptions = recipientOptions.filter(
    (option) => notificationRecipientIds.includes(option.value)
  )

  return (
    <div className="space-y-5">
      <BaseTextInput
        name="campaign-name"
        label="Campaign Name"
        value={name}
        onChange={(value) => onNameChange(String(value))}
        placeholder="Enter campaign name"
        noMargin
        disabled={false}
      />

      {!hideCampaignType && (
        <div>
          <label htmlFor="campaign-type" className={fieldLabelClassName}>
            Campaign Type
          </label>
          <ReactSelectWrapper
            inputId="campaign-type"
            instanceId="campaign-type"
            menuPosition="fixed"
            isLoading={campaignTypeLoading}
            isClearable={!campaignTypeDisabled}
            isDisabled={campaignTypeDisabled}
            options={campaignTypeOptions}
            value={selectedCampaignTypeOption}
            onChange={(option) =>
              onCampaignTypeChange(
                ((option as CampaignSelectOption<TCampaignType> | null)
                  ?.value ?? null) as TCampaignType | null
              )
            }
            placeholder={
              campaignTypeLoading
                ? 'Loading campaign types...'
                : 'Select campaign type'
            }
            noOptionsMessage={() =>
              campaignTypeLoading
                ? 'Loading campaign types...'
                : 'No campaign types found'
            }
          />
        </div>
      )}

      <div>
        <label htmlFor="campaign-product" className={fieldLabelClassName}>
          Product
        </label>
        <ReactSelectWrapper
          inputId="campaign-product"
          instanceId="campaign-product"
          menuPosition="fixed"
          isLoading={productLoading}
          isClearable
          isDisabled={productDisabled}
          options={productOptions}
          value={selectedProductOption}
          onChange={(option) =>
            onProductChange(
              ((option as CampaignSelectOption<TProduct> | null)?.value ??
                null) as TProduct | null
            )
          }
          placeholder={
            productLoading ? 'Loading products...' : 'Select product'
          }
          noOptionsMessage={() =>
            productLoading ? 'Loading products...' : 'No products found'
          }
        />
      </div>

      <div>
        <label
          htmlFor="campaign-launch-date"
          className="mb-2 block select-none text-base font-medium text-fg"
        >
          Projected Launch Date
        </label>
        <div
          className={
            launchDateType === 'skip' || launchDateDisabled
              ? 'pointer-events-none opacity-40'
              : ''
          }
        >
          <DatePicker
            value={launchDate || undefined}
            onChange={(value) => {
              onLaunchDateChange(value ?? '')
              if (value) onLaunchDateTypeChange('set')
            }}
            allowTyping
            className="!mt-0"
          />
        </div>
        <label className="mt-2 flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={launchDateType === 'skip'}
            onChange={(e) => {
              if (launchDateDisabled) return
              if (e.target.checked) {
                onLaunchDateTypeChange('skip')
                onLaunchDateChange('')
              } else {
                onLaunchDateTypeChange('set')
              }
            }}
            disabled={launchDateDisabled}
            className="size-4 cursor-pointer rounded border-line-form text-brand accent-brand"
          />
          <span className="text-xs text-fg-secondary">
            Decide later — set the date before launch
          </span>
        </label>
      </div>

      <div>
        <label htmlFor="campaign-owner" className={fieldLabelClassName}>
          Campaign Owner{' '}
          <span className="text-sm font-normal italic text-fg">(optional)</span>
        </label>
        <ReactSelectWrapper
          inputId="campaign-owner"
          instanceId="campaign-owner"
          menuPosition="fixed"
          isClearable
          isDisabled={ownerDisabled}
          isLoading={ownerLoading}
          options={ownerOptions}
          value={selectedOwnerOption}
          onChange={(option) =>
            onOwnerChange((option as CampaignOwnerOption | null)?.value ?? null)
          }
          placeholder={ownerLoading ? 'Loading users...' : 'Select...'}
          noOptionsMessage={() =>
            ownerLoading ? 'Loading users...' : 'No users found'
          }
          formatOptionLabel={formatUserOptionLabel}
        />
      </div>

      {onNotificationRecipientsChange && (
        <div>
          <label
            htmlFor="campaign-notification-recipients"
            className={fieldLabelClassName}
          >
            Notification Recipients{' '}
            <span className="text-sm font-normal italic text-fg">
              (optional)
            </span>
          </label>
          <ReactSelectWrapper
            inputId="campaign-notification-recipients"
            instanceId="campaign-notification-recipients"
            menuPosition="fixed"
            isMulti
            isClearable
            isLoading={ownerLoading}
            options={recipientOptions}
            value={selectedNotificationRecipientOptions}
            onChange={(options) =>
              onNotificationRecipientsChange(
                ((options ?? []) as CampaignOwnerOption[]).map(
                  (option) => option.value
                )
              )
            }
            placeholder={ownerLoading ? 'Loading users...' : 'Select users...'}
            noOptionsMessage={() =>
              ownerLoading ? 'Loading users...' : 'No users found'
            }
            formatOptionLabel={formatUserOptionLabel}
          />
          <p className="mt-1.5 text-xs text-fg-secondary">
            Users who get an email when this campaign receives an interested
            reply. Leave empty to alert the campaign owner. Adding recipients
            replaces the owner, so add the owner here too if you still want them
            notified. Each recipient must also have campaign notifications
            enabled in Settings &gt; Notifications.
          </p>
        </div>
      )}

      <div>
        <label htmlFor="campaign-description" className={fieldLabelClassName}>
          Description
        </label>
        <textarea
          id="campaign-description"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          disabled={descriptionDisabled}
          rows={4}
          maxLength={255}
          placeholder="Description"
          className="w-full rounded-lg border border-line bg-surface px-3 py-2.5 text-sm text-fg outline-none transition-colors placeholder:text-fg-muted focus:border-brand focus:ring-1 focus:ring-brand disabled:cursor-not-allowed disabled:bg-surface-hover disabled:opacity-60"
        />
      </div>

      <div className="w-full">
        <label className={fieldLabelClassName}>Color</label>
        <div
          className={
            colorDisabled
              ? 'pointer-events-none mx-auto max-w-[24rem] opacity-60'
              : 'mx-auto max-w-[24rem]'
          }
        >
          <ColorPicker
            label=""
            colors={MULTI_VALUE_SELECT_COLORS}
            value={color}
            onChange={onColorChange}
            noMargin
          />
        </div>
      </div>
    </div>
  )
}

export function getColorOption(color?: string | null) {
  return (
    MULTI_VALUE_SELECT_COLORS.find(
      (option) => option.background.toLowerCase() === color?.toLowerCase()
    ) ?? MULTI_VALUE_SELECT_COLORS[0]
  )
}

export function getCampaignOwnerDisplayName(user: UserType): string {
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ')
  return fullName || user.email
}

export function getCampaignOwnerInitials(user: UserType): string {
  if (user.first_name && user.last_name) {
    return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
  }

  if (user.first_name) {
    return user.first_name[0].toUpperCase()
  }

  return user.email[0]?.toUpperCase() ?? '?'
}

/**
 * Build the sorted Campaign Owner select options from a company's users.
 * Shared by the create and edit campaign modals so both build identically.
 *
 * @param users - The company's users (from `useUsers`).
 * @param excludeLimited - When true (default), drop Limited users —
 *   they can't own campaigns (FIR-8017). Pass false for the notification-
 *   recipients list, where limited users are still valid.
 * @returns Owner options sorted case-insensitively by display name.
 */
export function buildCampaignOwnerOptions(
  users: UserType[],
  { excludeLimited = true }: { excludeLimited?: boolean } = {}
): CampaignOwnerOption[] {
  return users
    .filter((user) => (excludeLimited ? !isLimitedUser(user) : true))
    .sort((a, b) =>
      getCampaignOwnerDisplayName(a).localeCompare(
        getCampaignOwnerDisplayName(b),
        undefined,
        { sensitivity: 'base' }
      )
    )
    .map((user) => ({
      value: user.id,
      label: getCampaignOwnerDisplayName(user),
      email: user.email,
      initials: getCampaignOwnerInitials(user),
    }))
}
